import type { Event as BrowserEvent, EventHint as BrowserEventHint } from '@sentry/types'
import type { ErrorEvent as NodeErrorEvent, EventHint as NodeEventHint, Scope as NodeScope } from '@sentry/node'
import { IGNORE_ERRORS, SENTRY_REQUEST_HEADER_ALLOWLIST, SENTRY_REQUEST_HEADER_TAGS } from '@/utils/constants/sentry'

type CombinedEventHint = BrowserEventHint | NodeEventHint
type BrowserRouteName = string | symbol | null | undefined
type MinimalEvent = {
  message?: string
  exception?: { values?: Array<{ value?: string | null }> }
  extra?: Record<string, unknown>
  user?: {
    ip?: string
    ip_address?: string
  }
  request?: {
    headers?: Record<string, string | number | boolean | null | undefined>
  }
}
type NavigatorWithConnection = Navigator & {
  connection?: {
    effectiveType?: string
    downlink?: number
    rtt?: number
    saveData?: boolean
  }
}
type ExceptionLike = {
  cause?: unknown
  message?: unknown
  status?: unknown
  statusCode?: unknown
  statusMessage?: unknown
  value?: unknown
  name?: unknown
  response?: {
    headers?: Headers | Record<string, string | number | boolean | null | undefined>
  }
}
type ScriptErrorTarget = {
  src: string
  id: string
  type: string
  async: boolean
  dataset: Record<string, string>
  headScripts: Array<{
    src: string
    id: string
    type: string
  }>
}
type SentryServerRequestContext = {
  method: string
  path: string
  url: string
  queryKeys: string[]
  route?: string
  clientIp?: string
  headers: Record<string, string | undefined>
}

const normalizeMessage = (value: unknown) => {
  return String(value)
    .trim()
    .replace(/ +(?= )/g, '')
}

const getOriginalExceptionMessages = (exception: unknown) => {
  if (!exception) return []

  if (typeof exception === 'string') return [exception]

  if (exception instanceof Error) {
    return [exception.message, exception.toString()]
  }

  if (typeof exception === 'object') {
    const { message, value, name } = exception as ExceptionLike

    return [message, value, name].filter((item): item is string => typeof item === 'string')
  }

  return []
}

const isExcludedError = (event: MinimalEvent, hint: CombinedEventHint) => {
  const messages = [
    event.message,
    ...(event.exception?.values ?? []).map(({ value }) => value),
    ...getOriginalExceptionMessages(hint.originalException)
  ]
    .filter(Boolean)
    .map(normalizeMessage)

  return IGNORE_ERRORS.some((ignoredError) => {
    return messages.some((message) =>
      ignoredError instanceof RegExp ? ignoredError.test(message) : message.includes(ignoredError)
    )
  })
}

const isNon404Status = (value: unknown) => {
  if (typeof value === 'number') return value !== 404

  return typeof value === 'string' && /^\d{3}$/.test(value) && value !== '404'
}

const hasUnderlyingNon404Error = (exception: ExceptionLike) => {
  const processedCauses = new Set<object>()
  let cause = exception.cause

  while (cause && typeof cause === 'object' && !processedCauses.has(cause)) {
    processedCauses.add(cause)

    const { status, statusCode: causeStatusCode, cause: nestedCause } = cause as ExceptionLike
    if (isNon404Status(causeStatusCode) || isNon404Status(status)) return true

    cause = nestedCause
  }

  return false
}

const isHandledPageNotFound = (exception: unknown) => {
  if (!exception || typeof exception !== 'object') return false

  const error = exception as ExceptionLike

  return (
    error.statusCode === 404 && error.statusMessage === 'global.error.pageNotFound' && !hasUnderlyingNon404Error(error)
  )
}

const getRequestHeader = (event: MinimalEvent, headerName: string) => {
  const headers = event.request?.headers
  if (!headers) return ''

  const normalizedHeaderName = headerName.toLowerCase()
  const header = Object.entries(headers).find(([key]) => key.toLowerCase() === normalizedHeaderName)

  return header ? String(header[1]) : ''
}

const isKnownCrawlerUserAgent = (userAgent: string) => {
  return /(?:googlebot|adsbot-google|apis-google|mediapartners-google|bingbot|duckduckbot|yandexbot|baiduspider)/i.test(
    userAgent
  )
}

const isNuxtBuildMetaGatewayTimeout = (event: MinimalEvent, hint: CombinedEventHint) => {
  const messages = [
    event.message,
    ...(event.exception?.values ?? []).map(({ value }) => value),
    ...getOriginalExceptionMessages(hint.originalException)
  ]
    .filter(Boolean)
    .map(normalizeMessage)

  const matchesBuildMetaTimeout = messages.some((message) => {
    return (
      message.includes('FetchError: [GET]') &&
      /\/_nuxt\/builds\/meta\/[^"]+\.json/.test(message) &&
      message.includes('504 Gateway Timeout')
    )
  })

  if (!matchesBuildMetaTimeout) return false

  const userIp = event.user?.ip ?? event.user?.ip_address ?? ''
  const userAgent = getRequestHeader(event, 'user-agent')

  return userIp.startsWith('2607:f8b0:') || isKnownCrawlerUserAgent(userAgent)
}

const getResponseHeader = (
  headers: Headers | Record<string, string | number | boolean | null | undefined>,
  headerName: string
) => {
  if (headers instanceof Headers) return headers.get(headerName) ?? ''

  const normalizedHeaderName = headerName.toLowerCase()
  const header = Object.entries(headers).find(([key]) => key.toLowerCase() === normalizedHeaderName)

  return header ? String(header[1]) : ''
}

const addResponseHeaderContext = <T extends MinimalEvent>(sentryEvent: T, exception: unknown) => {
  if (!exception || typeof exception !== 'object') return

  const responseHeaders = (exception as ExceptionLike).response?.headers
  if (!responseHeaders) return

  const headers = [
    'cf-ray',
    'cf-cache-status',
    'cf-request-id',
    'server',
    'x-cache',
    'x-cache-status',
    'x-proxy-cache',
    'x-upstream-cache-status',
    'age',
    'cache-control',
    'content-type',
    'content-length',
    'etag',
    'last-modified',
    'vary',
    'date'
  ].reduce<Record<string, string>>((accumulator, headerName) => {
    const headerValue = getResponseHeader(responseHeaders, headerName)

    if (headerValue) {
      accumulator[headerName] = headerValue
    }

    return accumulator
  }, {})

  if (!Object.keys(headers).length) return

  sentryEvent.extra = {
    ...sentryEvent.extra,
    responseHeaders: {
      ...(typeof sentryEvent.extra?.responseHeaders === 'object' && sentryEvent.extra.responseHeaders !== null
        ? sentryEvent.extra.responseHeaders
        : {}),
      ...headers
    }
  }
}

const getScriptErrorTarget = (exception: unknown): ScriptErrorTarget | null => {
  const window = globalThis.window
  const document = globalThis.document

  if (window === undefined || document === undefined || !(exception instanceof window.Event)) return null

  const target = exception.target
  if (!(target instanceof window.HTMLScriptElement)) return null

  return {
    src: target.src,
    id: target.id,
    type: target.type,
    async: target.async,
    dataset: Object.fromEntries(
      Object.entries(target.dataset).filter((entry): entry is [string, string] => typeof entry[1] === 'string')
    ),
    headScripts: Array.from(document.head.querySelectorAll('script'))
      .slice(-10)
      .map((script) => ({
        src: script.src,
        id: script.id,
        type: script.type
      }))
  }
}

const addScriptErrorContext = <T extends BrowserEvent | NodeErrorEvent>(sentryEvent: T, exception: unknown) => {
  const scriptErrorTarget = getScriptErrorTarget(exception)

  if (!scriptErrorTarget) return

  sentryEvent.extra = {
    ...sentryEvent.extra,
    scriptErrorTarget
  }
}

export const pickSentryRequestHeaders = (requestHeaders: Record<string, string | undefined>) => {
  return Object.fromEntries(
    SENTRY_REQUEST_HEADER_ALLOWLIST.flatMap((header) => {
      const value = requestHeaders[header]
      return value ? [[header, value]] : []
    })
  )
}

export const addBrowserContext = (sentryEvent: BrowserEvent, routeName?: BrowserRouteName) => {
  const window = globalThis.window
  const document = globalThis.document
  const navigator = globalThis.navigator

  if (window === undefined || document === undefined || navigator === undefined) return

  const connection = (navigator as NavigatorWithConnection).connection

  sentryEvent.contexts = {
    ...sentryEvent.contexts,
    browser_page: {
      url: `${window.location.origin}${window.location.pathname}`,
      path: window.location.pathname,
      queryKeys: Array.from(new URLSearchParams(window.location.search).keys()),
      hash: window.location.hash,
      referrer: document.referrer,
      title: document.title,
      routeName: routeName ? String(routeName) : undefined
    },
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      orientation: window.screen.orientation?.type
    },
    browser_capabilities: {
      language: navigator.language,
      languages: navigator.languages,
      cookieEnabled: navigator.cookieEnabled,
      hardwareConcurrency: navigator.hardwareConcurrency,
      maxTouchPoints: navigator.maxTouchPoints,
      online: navigator.onLine,
      connection
    }
  }

  sentryEvent.tags = {
    ...sentryEvent.tags,
    route: window.location.pathname,
    locale: navigator.language
  }
}

export const setServerRequestScope = (scope: NodeScope, request: SentryServerRequestContext) => {
  const headers = pickSentryRequestHeaders(request.headers)

  scope.setContext('request', {
    method: request.method,
    path: request.path,
    url: request.url,
    queryKeys: request.queryKeys,
    route: request.route,
    clientIp: request.clientIp
  })

  scope.setContext('request_headers', headers)

  for (const header of SENTRY_REQUEST_HEADER_TAGS) {
    const value = headers[header]
    if (value) scope.setTag(header, value)
  }
}

export function beforeSend(
  sentryEvent: NodeErrorEvent,
  sentryHint: NodeEventHint
): NodeErrorEvent | PromiseLike<NodeErrorEvent | null> | null

export function beforeSend(
  sentryEvent: BrowserEvent,
  sentryHint: BrowserEventHint
): BrowserEvent | PromiseLike<BrowserEvent | null> | null

export function beforeSend<T extends BrowserEvent | NodeErrorEvent>(
  sentryEvent: T,
  sentryHint: CombinedEventHint
): T | PromiseLike<T | null> | null {
  try {
    addResponseHeaderContext(sentryEvent, sentryHint.originalException)
    addScriptErrorContext(sentryEvent, sentryHint.originalException)

    if (
      !sentryHint.originalException ||
      isExcludedError(sentryEvent, sentryHint) ||
      isHandledPageNotFound(sentryHint.originalException) ||
      isNuxtBuildMetaGatewayTimeout(sentryEvent, sentryHint)
    ) {
      return null
    }
  } catch (error) {
    console.warn('[sentry] Failed to identify originalException', error)
  }

  return sentryEvent
}
